import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Col, convertToKRW, getItemWithExpiration, Row } from '../../lib/utils';
import { Body2Bold, Body3Regular, CaptionLight, Subtitle1Bold } from '../../lib/font';
import ColTitleAndTextInput from '../ColTitleAndTextInput';
import dayjs from 'dayjs';
import MemoTextInput from '../TextInput/MemoTextInput';
import { MemberMembershipDataType } from '../../services/useMembershipQueryService';
import Modal from '../modal/Modal';
import { colors } from '../../lib/colors';
import ModalUpdateMembershipName from './ModalUpdateMembershipName';
import useMembershipService from '../../services/useMembershipService';
import { useQueryClient } from '@tanstack/react-query';
import Dropdown from '../DropDown/DropDown';
import { RegularText } from '../../lib/Text';
import ModalSalesManagerName from './ModalSalesManagerName';
import ModalUpdateAlert from './ModalUpdateAlert';
import useSingleQuery from '../../hook/useSingleQuery';
import { fetchGetSalesManagers, SalesManager } from '../../services/useMembershipPaymentsQueryService';
import useMembershipPaymentsMutationService from '../../services/useMembershipPaymentsMutationService';
import { CodeValueType } from '../../types/types';
import NewCalendar from '../Calendar/NewCalendar';
import SvgIcon from '../SvgIcon';
import { ModalAlertMemberType } from './ModalMembershipCreate';
import { ExpiryPolicy } from '@/features/groupTicket/types';
import {
    buildSharedActiveParticipantImpactWarning,
    buildSharedActiveSessionDecreaseWarning,
    buildSharedActiveSessionIncreaseWarning,
    buildSharedActiveUpdateBody,
    getParticipantImpactChanges,
    buildSharedMinimumTotalSessionMessage,
    isSharedMembershipLifecycleMutationBlocked,
    SharedActiveEditableFormField,
    SharedMembershipUpdateRestriction,
} from '../../features/sharedMembership/editRestrictions';
import { ShareLifecycleUsage } from '../../features/sharedMembership/shareLifecycle';
import { resolveSharedMembershipUpdateFailure } from '../../features/sharedMembership/errors';
import {
    buildSettlementImpactConfirmMessage,
    getSettlementImpactChanges,
} from '../../features/sharedMembership/settlementImpact';
import { useCoachCommissionTables } from '../../features/coachCommissionTable/hooks';
import {
    hasReRegistrationExceptionRule,
    shouldCheckIssueKindFallbackNotice,
    ISSUE_KIND_FALLBACK_CHANGE_NOTICE,
} from '../../features/settlementClassification/settlementBasis';

/**
 * 공유 중이라 잠긴 입력을 감싼다.
 *
 * `ColTitleAndTextInput` 의 `disabled` 는 텍스트 입력에만 전달되고 달력 변형에는
 * 닿지 않는다(그쪽은 감싼 `Col` 의 onClick 이 달력을 연다). 공용 컴포넌트를 넓히는
 * 대신 여기서 포인터 이벤트를 끊는다 — 그 컴포넌트는 이 화면 밖에서도 쓰인다.
 */
const ShareLockedField = ({
    locked,
    children,
}: {
    locked: boolean;
    children: React.ReactNode;
}) => {
    if (!locked) return <>{children}</>;
    return (
        <div
            className="pointer-events-none opacity-40"
            aria-disabled
            title="공유 중에는 바꿀 수 없어요"
        >
            {children}
        </div>
    );
};

const EXPIRY_POLICY_OPTIONS: { value: ExpiryPolicy; label: string; desc: string }[] = [
    { value: 'FIRST', label: '둘 중 빠른 것', desc: '회차/기간 중 먼저 도달' },
    { value: 'COUNT', label: '회차 소진 시', desc: '횟수 다 쓰면 만료' },
    { value: 'PERIOD', label: '기간 만료 시', desc: '기간 끝나면 만료' },
];

type Props = {
    memberName?: string;
    coachName?: string;
    membership: MemberMembershipDataType;
    setIsVisible: Dispatch<SetStateAction<boolean>>;
    createAlert: any;
    /** App.tsx 공통 두 버튼 알럿. 정산이 함께 바뀌는 저장을 확인받는 데 쓴다. */
    createTwoButtonAlert?: (
        title: string,
        content: string,
        isError: boolean,
        cancelButtonText?: string,
        confirmButtonText?: string,
        cancelCallback?: () => void,
        confirmCallback?: () => void,
        isExitButton?: boolean,
    ) => void;
    /** 저장 자체가 막힌 상태. 이게 있으면 모달은 안내만 하고 아무것도 저장하지 않는다. */
    updateRestriction?: SharedMembershipUpdateRestriction | null;
    /**
     * 서버 409(`SHARED_MEMBERSHIP_LIFECYCLE_MUTATION_BLOCKED`) 안내 문구.
     * 일부만 열린 상태에서도 서버 판정이 그 사이 바뀌면 409가 날 수 있어 따로 받는다.
     */
    lifecycleBlockedNotice?: SharedMembershipUpdateRestriction | null;
    /**
     * 공유 **중** 부분 편집. 값이 있으면 모달을 열되 `editableFields` 이외의 입력을 잠근다.
     *
     * 서버는 보호 필드가 **본문에 들어 있기만 해도** 409를 낸다(값이 같아도 `hasOwnProperty`
     * 판정). 그래서 잠그는 것으로 끝나지 않고 **전송 본문도 이 목록으로 좁힌다.**
     */
    sharedActiveEdit?: {
        editableFields: SharedActiveEditableFormField[];
        notice: SharedMembershipUpdateRestriction;
    } | null;
    /**
     * 바꾸면 지나간 달 정산이 함께 바뀌는 필드(서버 `shareLifecycle.settlementImpactFields`).
     * 없으면 확인 알럿을 띄우지 않는다 — 경고를 못 하는 것이지 저장을 막을 이유는 없다.
     */
    settlementImpactFields?: string[] | null;
    /**
     * 바꾸면 공유 참여자에게 영향이 가는 필드(서버 `shareLifecycle.participantImpactFields`).
     * 없으면 경고를 띄우지 않는다 — 경고를 못 하는 것이지 저장을 막을 이유는 없다.
     */
    participantImpactFields?: string[] | null;
    /**
     * 공유 사용량 스냅샷(서버 `shareLifecycle.sharedUsage`). 총 회차 하한과 그 근거다.
     * 과거·현재 공유 참여자가 쓴 회차를 침범하지 않는 값이며, 프론트에서 계산하지 않는다.
     */
    sharedUsage?: ShareLifecycleUsage | null;
    /**
     * 업그레이드 화면으로 보내는 콜백.
     *
     * 공유 중에는 총 회차를 **줄이는 것만** 된다. 늘리는 건 추가 결제가 필요해
     * 업그레이드가 담당한다. "안 됩니다"로 끝내면 관리자가 갈 곳을 잃으므로
     * 다음 행동을 같이 준다. 없으면 안내만 하고 이동 버튼을 그리지 않는다.
     */
    onRequestUpgrade?: () => void;
};

const ModalMembershipUpdate = ({
    memberName,
    coachName,
    membership,
    setIsVisible,
    createAlert,
    createTwoButtonAlert,
    updateRestriction,
    lifecycleBlockedNotice,
    sharedActiveEdit,
    settlementImpactFields,
    participantImpactFields,
    sharedUsage,
    onRequestUpgrade,
}: Props) => {
    /**
     * 공유 중 부분 편집 모드인가. 잠금·전송 본문·검증 건너뛰기가 전부 이 하나로 갈린다.
     */
    const isSharedLimitedEdit = !!sharedActiveEdit;
    const sharedEditableFields = useMemo(
        () => new Set<string>(sharedActiveEdit?.editableFields ?? []),
        [sharedActiveEdit],
    );
    /** 공유 중이라서 이 입력이 잠겼는가. 부분 편집 모드가 아니면 아무것도 잠기지 않는다. */
    const isLockedByShare = useCallback(
        (field: string) => isSharedLimitedEdit && !sharedEditableFields.has(field),
        [isSharedLimitedEdit, sharedEditableFields],
    );
    const minimumTotalSession = sharedUsage?.minimumTotalSession ?? null;
    /** 공유 중에 총 회차 입력이 실제로 열려 있는가. */
    const isSharedSessionEditable = isSharedLimitedEdit && sharedEditableFields.has('totalSession');
    const [authObject, setAuthObject] = useState(getItemWithExpiration('authObject'));
    const [authorization, setAuthorization] = useState(getItemWithExpiration('authorization'));
    const queryClient = useQueryClient();
    // 판매담당자이름
    const [salesManagerName, setSalesManagerName] = useState<string>('');

    const { postSalesManagerMutation } = useMembershipPaymentsMutationService({
        onPostSalesManagerSuccess(data) {
            setSalesManager(resfineSalesManagers(data));

            // salesManagerName 으로 data 에서 userName 같은거 찾아서 setSalesManagerName
            data.forEach((item: any) => {
                if (item.userName === salesManagerName) {
                    setSelectedSalesManager(item);
                    return;
                }
            });
        },
    });
    const { refinedResult, isLoading, isFetching } = useSingleQuery({
        fetchFn: fetchGetSalesManagers,
        params: { centerId: authObject.centerId },
        enabled: !!authorization,
        suspense: false,
    });

    // userId, userName -> code, value
    // 마지막에 직접입력 추가
    const resfineSalesManagers = useCallback(
        (data: any): Array<{ code: string; value: string; onClick?: () => void }> => {
            // userId, userName -> code, value 변환
            const _salesManagers = Array.isArray(data)
                ? data.map((item: any) => ({
                      code: item.userId,
                      value: item.userName,
                      object: item,
                  }))
                : [];

            // 직접입력 추가
            return [
                ..._salesManagers,
                {
                    code: 'direct',
                    value: '직접입력',
                    onClick: () => setIsSalesManagerModalVisible(true),
                },
            ];
        },
        []
    );
    const [salesManager, setSalesManager] = useState<CodeValueType[] | undefined>(undefined);

    useEffect(() => {
        if (!isLoading && !isFetching) {
            setSalesManager(resfineSalesManagers(refinedResult.data));
        }
    }, [isLoading, isFetching]);
    const { updateMemberShipMutation } = useMembershipService({
        onUpdatedMemberShipSuccess() {
            createAlert('수강권 수정 완료', '수강권이 성공적으로 수정되었습니다 !', false);
            queryClient.invalidateQueries({
                queryKey: ['getMembershipForMemberQuery'],
            });

            setIsVisible(false);
        },
        onUpdatedMemberShipFail(status, data) {
            // 종료 후 수정에서만 나오는 409 두 가지를 먼저 본다. 아래의
            // `status === 409 && updateRestriction` 는 "지금 수정이 막혀 있다"는 문구라
            // 이 둘에 붙으면 원인과 다른 안내가 나간다.
            const updateFailure = resolveSharedMembershipUpdateFailure({ status, data });
            if (updateFailure) {
                createAlert(updateFailure.title, updateFailure.message, true);
                return;
            }
            /**
             * 부분 편집 중에는 **공유 종료를 권하지 않는다.**
             *
             * 이 모드는 보호 필드를 아예 보내지 않는다. 그런데도 409가 왔다면 그 사이
             * 공유 상태나 사용 회차가 바뀐 것이지, 관리자가 종료해야 할 일이 아니다.
             * 여기서 "공유를 먼저 종료해주세요"를 띄우면 되돌릴 수 없는 동작을 잘못
             * 유도하게 된다. 원인을 모를 때는 영구 불가로 단정하지 않고 "지금은"으로 쓴다.
             *
             * 서버가 새 에러코드(공유 중 회차 하한·증가 금지)를 확정하면 위쪽
             * `resolveSharedMembershipUpdateFailure` 에 매핑이 붙어 이 폴백보다 먼저 잡힌다.
             */
            if (isSharedLimitedEdit && (isSharedMembershipLifecycleMutationBlocked(data) || status === 409)) {
                createAlert(
                    '지금은 저장할 수 없어요',
                    '공유 상태나 사용된 회차가 그 사이 바뀌었을 수 있어요.\n화면을 새로고침한 뒤 다시 시도해주세요.',
                    true
                );
                return;
            }
            const blockedNotice = updateRestriction ?? lifecycleBlockedNotice;
            if (
                isSharedMembershipLifecycleMutationBlocked(data) ||
                (status === 409 && blockedNotice)
            ) {
                createAlert(
                    blockedNotice?.title ?? '공유 중에는 수정할 수 없어요',
                    blockedNotice?.message ??
                        '공유 수강권 정보가 변경되었습니다. 수강권 상세를 새로고침한 뒤 공유 상태를 확인해주세요.',
                    true
                );
                return;
            }
            if (status === 412) {
                createAlert(
                    '수강권 관리 권한이 없어요',
                    '센터 관리자에게 수강권 수정 권한을 요청한 뒤 다시 시도해주세요.',
                    true
                );
                return;
            }
            createAlert(
                '수강권 수정 실패',
                status
                    ? `요청을 처리하지 못했어요. 잠시 후 다시 시도해주세요. (오류 코드 ${status})`
                    : '네트워크 연결을 확인한 뒤 다시 시도해주세요.',
                true
            );
        },
    });
    // 수강권 이름 수정 모달
    const [isMembershipNameModalVisible, setIsMembershipNameModalVisible] = useState(false);
    // 판매 담당자 이름 추가 모달
    const [isSalesManagerModalVisible, setIsSalesManagerModalVisible] = useState(false);
    // 수정시 경고 모달
    const [isAlertModalVisible, setIsAlertModalVisible] = useState(false);
    // 한번만 수정시 경고 줘야함
    const [isCheckUpdate, setIsCheckUpdate] = useState(false);
    // 달력 열기
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [isMemberTypeModalVisible, setIsMemberTypeModalVisible] = useState(false);
    // 필수 입력값 에러 여부
    const [isRequiredError, setIsRequiredError] = useState<boolean>(false);

    // 회당 가격
    const [pricePerClass, setPricePerClass] = useState<number>(membership.pricePerSchedule);
    // 판매 금액
    const [totalPrice, setTotalPrice] = useState<number>(membership.totalPrice);

    // 시작 날짜
    const [startDate, setStartDate] = useState<string>(membership.activationDate);
    // 만료 날짜 expirationDate
    const [expirationDate, setExpirationDate] = useState<string>(membership.expirationDate);
    // 수강권 기간
    const [membershipPeriod, setMembershipPeriod] = useState<number>(
        dayjs(expirationDate).diff(dayjs(startDate), 'day')
    );
    // 시작 회차 startSession
    const [startSession, setStartSession] = useState<number>(membership.startSessionValue);
    // 총 회차 totalSession
    const [totalSession, setTotalSession] = useState<number>(membership.totalSession);
    // 수업 시간
    const [classTime, setClassTime] = useState<number>(membership.period);
    // 메모
    const [memo, setMemo] = useState<string>(membership.memo);
    // BGColor
    const [BGColor, setBGColor] = useState<string[]>(membership.BGColor);
    // 수강권 이름
    const [membershipName, setMembershipName] = useState<string>(membership.name);
    // 결제일자
    const [paymentDate, setPaymentDate] = useState<string>(membership?.initialPaymentDate ?? dayjs().toISOString());
    // 판매담당자
    const [selectedSalesManager, setSelectedSalesManager] = useState<SalesManager | undefined>(membership.salesManager);
    const hasChangedTransferredMembershipSalesManager =
        !!membership.transferredFromMembershipId &&
        !!selectedSalesManager &&
        selectedSalesManager.userId !== membership.salesManager?.userId;
    // 회원 구분
    const [memberType, setMemberType] = useState<string>(membership?.isNewSales ? 'new' : 're');
    /*
     * 옛 수강권(발급 유형 기록 없음)의 신규/재등록 표시를 바꾸면, 재등록 예외 규칙이 있는 상세·세션별 정산 센터에서는 지난 달 급여도
     * 바뀔 수 있다(서버가 이 표시로 재등록 수업료를 정한다). 바꿨을 때만 이번 달 급여 테이블을 읽어 규칙이 있는지 본다.
     * 규칙이 없다고 확인될 때만 안내를 숨긴다(읽는 중·못 읽으면 안내한다 — 놓치는 쪽이 더 위험).
     * 한계: 이번 달 규칙만 본다. 지난달에만 있던 재등록 규칙(이번 달에 해제)은 여기서 보이지 않는다.
     */
    const isIssueKindDisplayChanged = memberType !== 'none' && memberType !== (membership?.isNewSales ? 'new' : 're');
    // 세션별 정산도 상세 정산과 같은 재등록 대체 판정을 쓴다(서버 exceptionScheduleFee — 새 서버). 기본 정산은 원래부터
    // isNewSales 로 신규/재등록 요율을 고르는 기존 동작이고 이 안내는 재등록 「예외」 규칙 전용이라 넣지 않는다(기본 정산은 재등록 예외를 안 쓴다).
    const shouldCheckIssueKindFallback = shouldCheckIssueKindFallbackNotice(
        authObject?.settlementType,
        isIssueKindDisplayChanged,
        membership,
    );
    const reRegistrationRuleTables = useCoachCommissionTables({
        centerId: authObject?.centerId ?? '',
        year: dayjs().format('YYYY'),
        month: dayjs().format('MM'),
        enabled: shouldCheckIssueKindFallback,
    });
    // 다시 읽기가 실패하면 React Query 는 이전 데이터를 남긴다 — 오류면 이전 「규칙 없음」을 믿지 않고 안내한다.
    const showIssueKindFallbackNotice =
        shouldCheckIssueKindFallback &&
        (reRegistrationRuleTables.isError || hasReRegistrationExceptionRule(reRegistrationRuleTables.data) !== false);
    // 분할 결제 여부
    const isSplitPayment = membership?.isSplitPayment ?? false;
    // 결제 금액
    const [paymentAmount, setPaymentAmount] = useState<number>(membership?.totalPaymentAmount ?? 0);
    // 미수금액
    const [unpaidAmount, setUnpaidAmount] = useState<number>(membership?.totalUnpaidAmount ?? 0);
    // 만료 정책 (그룹/PT 모두 — PT 레거시 기본은 'COUNT')
    const [expiryPolicy, setExpiryPolicy] = useState<ExpiryPolicy>(
        ((membership as any)?.expiryPolicy as ExpiryPolicy) ??
            (membership?.source === 'group' ? 'FIRST' : 'COUNT')
    );
    const isGroup = membership?.source === 'group';

    /**
     * 정산 영향 비교의 기준값 — **수강권 문서가 아니라 폼의 초기 상태**다.
     *
     * 문서와 비교하면 값이 비어 있던 레거시 레코드에서 오탐이 난다. 예를 들어
     * `isSplitPayment`·`isNewSales` 가 undefined 면 폼은 false 로 시작하는데, 문서와
     * 비교하면 사용자가 아무것도 건드리지 않아도 "바뀌었다"가 된다. 경고가 잦으면
     * 아무도 읽지 않는다. 기준은 "관리자가 이번에 무엇을 건드렸나" 하나다.
     */
    /**
     * 만료 정책의 **폼 초기값**. 문서와 비교하면 값이 비어 있던 레거시에서 폼 기본값
     * ('COUNT')과 어긋나 아무것도 안 건드려도 경고가 뜬다. 정산 경고와 같은 원칙이다.
     */
    const initialExpiryPolicyRef = useRef<ExpiryPolicy | null>(null);
    const initialSettlementValuesRef = useRef<Record<string, unknown> | null>(null);
    if (initialExpiryPolicyRef.current === null) initialExpiryPolicyRef.current = expiryPolicy;
    if (initialSettlementValuesRef.current === null) {
        initialSettlementValuesRef.current = {
            pricePerSchedule: pricePerClass,
            totalPrice,
            totalSession,
            isNewSales: memberType === 'new',
            initialPaymentDate: paymentDate,
            isSplitPayment,
            salesManager: selectedSalesManager,
        };
    }

    const onClickUpdateMembership = () => {
        if (updateRestriction) {
            createAlert(updateRestriction.title, updateRestriction.message, true);
            return;
        }
        /**
         * 검증은 **그 입력이 열려 있을 때만** 건다.
         *
         * 잠긴 값끼리 비교하는 검사는 이번 편집과 무관하다. 예전 데이터가 어긋난
         * 수강권(예: 시작회차 > 총회차)에서 그대로 두면, 공유 중에 메모 한 줄 고치는 것도
         * "올바르지 않은 입력"으로 막힌다 — 고칠 방법이 없는 이유로 막는 자리가 된다.
         */
        // 수업시간이 10분 단위가 아니라면 경고창
        if (!isLockedByShare('period') && classTime % 10 !== 0) {
            createAlert('올바르지 않은 입력', '수업 시간을 10분단위로 입력하여 주세요', true);

            return;
        }
        // 시작회차가 총회차 보다 크다면 경고창
        else if (
            (!isLockedByShare('totalSession') || !isLockedByShare('startSessionValue')) &&
            startSession > totalSession
        ) {
            createAlert('올바르지 않은 입력', '시작회차를 총회차보다 낮게 설정해주세요!', true);

            return;
        }
        //시작날짜가 만료날짜보다 크다면 경고창
        else if (
            (!isLockedByShare('activationDate') || !isLockedByShare('expirationDate')) &&
            dayjs(startDate).isAfter(dayjs(expirationDate))
        ) {
            createAlert('올바르지 않은 입력', '시작날짜를 만료날짜보다 더 낮게 설정해주세요!', true);

            return;
        }
        // 회원 구분이 code 까 none일경우
        else if (!isLockedByShare('isNewSales') && memberType === 'none') {
            setIsMemberTypeModalVisible(true);
            setIsRequiredError(true);
            return;
        }
        // 공유 회차가 잡혀 있는 수강권은 이미 쓰였거나 예약된 회차 아래로 총 회차를 줄일 수 없다.
        // 서버가 하한(sharedUsage.minimumTotalSession)을 미리 내려주므로 여기서 막아 409를 안 보게 한다.
        // 공유 **중**과 종료 후가 같은 규칙이고, 숫자는 전부 서버 값이다.
        //
        // 시작 회차를 함께 바꾸면 서버가 하한을 그 값 기준으로 다시 계산한다. 낡은 하한으로
        // 막으면 서버가 허용하는 저장을 화면에서만 못 하게 되므로, 그때는 서버 판정에 맡긴다
        // (거절되면 SHARED_MEMBERSHIP_CLOSED_CAPACITY_CONFLICT 안내가 나간다).
        if (
            sharedUsage &&
            startSession === membership.startSessionValue &&
            totalSession < sharedUsage.minimumTotalSession
        ) {
            createAlert(
                '총 회차를 더 줄일 수 없어요',
                buildSharedMinimumTotalSessionMessage(sharedUsage),
                true
            );
            return;
        }
        /**
         * **메모를 비우는 것은 아직 저장되지 않는다.**
         *
         * `createDataObject`(api 계층)가 빈 문자열을 본문에서 걷어내기 때문이다. 공유와
         * 무관한 기존 결함이라 여기서 고치면 모든 수강권 수정의 동작이 바뀐다(별건 티켓).
         * 다만 공유 중 부분 편집에서는 메모가 주요 편집 대상이라, 저장했다고 말해놓고
         * 그대로 남는 것만은 막는다 — 거짓 성공 안내를 하지 않는다.
         */
        if (
            isSharedLimitedEdit &&
            sharedEditableFields.has('memo') &&
            memo.trim() === '' &&
            (membership.memo ?? '') !== ''
        ) {
            createAlert(
                '메모는 비울 수 없어요',
                '지금은 메모를 완전히 비우는 저장이 지원되지 않아요.\n내용을 바꾸거나, 공백 대신 한 글자라도 남겨주세요.',
                true
            );
            return;
        }
        // 판매담당자가 비어 있으면 저장하지 않던 기존 동작을 유지한다. 다만 공유 중
        // 부분 편집에서는 담당자를 못 고르는 수강권의 메모까지 조용히 막히므로,
        // 그때는 담당자 키를 본문에서 빼고 나머지를 저장한다.
        if (!isSharedLimitedEdit && selectedSalesManager === undefined) return;

        // 그룹 수강권이면 PT 필드 → MemberTicket 필드로 매핑됨 (api/memberShip.ts 내부 분기)
        const centerId = getItemWithExpiration('authObject')?.centerId;

        /**
         * 공유 **중**에는 서버가 허용한 필드만 싣는다.
         *
         * 서버는 보호 필드가 본문에 **들어 있기만 해도** 409를 낸다 — 값이 그대로여도
         * `hasOwnProperty` 로 판정한다. 폼 전체를 보내면 메모 한 줄만 고쳐도 회차·금액이
         * 함께 실려 거절된다. `$set` 부분 갱신이라 안 보낸 필드는 그대로 남는다.
         */
        const sharedLimitedParams = {
            membershipId: membership._id,
            accessToken: authorization,
            /**
             * **폼이 가진 값을 전부 넘긴다.** 무엇을 실을지는 `editableFields` 가 고른다.
             *
             * 예전에는 여기 네 개만 넘겨서, 서버가 허용 목록을 넓혀 화면이 가격·날짜를
             * 열어줘도 그 값이 요청에서 조용히 빠졌다. 서버는 나머지 본문을 200 으로
             * 받고 화면은 "수정 완료"를 띄운다 — 관리자는 바뀐 줄 알지만 장부는 그대로다.
             * 목록과 값 객체는 반드시 같이 넓혀야 한다.
             */
            ...buildSharedActiveUpdateBody(sharedActiveEdit?.editableFields ?? [], {
                pricePerSchedule: pricePerClass,
                totalPrice,
                activationDate: startDate,
                expirationDate,
                startSessionValue: startSession,
                totalSession,
                period: classTime,
                expiryPolicy,
                salesManager: selectedSalesManager,
                isNewSales: memberType === 'new',
                initialPaymentDate: paymentDate,
                memo,
            }),
        };

        const submit = () =>
            updateMemberShipMutation.mutate(
                isSharedLimitedEdit
                    ? sharedLimitedParams
                    : {
                          membershipId: membership._id,
                          name: membershipName,
                          period: classTime,
                          activationDate: startDate,
                          expirationDate: expirationDate,
                          startSessionValue: startSession,
                          totalSession: totalSession,
                          memo: memo,
                          pricePerSchedule: pricePerClass,
                          totalPrice: totalPrice,
                          accessToken: authorization,
                          isNewSales: memberType === 'new',
                          initialPaymentDate: paymentDate,
                          salesManager: selectedSalesManager,
                          isSplitPayment: isSplitPayment,
                          expiryPolicy,
                          ...(isGroup
                              ? {
                                    source: 'group' as const,
                                    centerId,
                                    // 잔여 회차는 서버가 총회차 증감분으로만 조정한다.
                                    // 완료 회차 기준 재계산은 이미 예약 차감된 미래 수업을 되살릴 수 있다.
                                }
                              : {}),
                      }
            );

        /**
         * 정산이 함께 바뀌는 저장은 확인을 받는다.
         *
         * 정산은 수강권 문서를 그 달의 스냅샷이 아니라 **현재 값**으로 읽는다. 그래서 단가나
         * 총 회차를 고치면 이미 마감된 달의 매출·지급액까지 같이 움직인다. 의도한 정정이면
         * 허용된 동작이지만, 모르고 바꾸면 그게 다음 CS 다.
         *
         * 어느 필드가 그런지는 서버가 알려준다(settlementImpactFields). 목록이 없으면
         * 확인을 건너뛰고 예전과 똑같이 저장한다.
         */
        const settlementImpactChanges = getSettlementImpactChanges({
            settlementImpactFields,
            before: initialSettlementValuesRef.current,
            after: {
                pricePerSchedule: pricePerClass,
                totalPrice,
                totalSession,
                isNewSales: memberType === 'new',
                initialPaymentDate: paymentDate,
                isSplitPayment,
                salesManager: selectedSalesManager,
            },
        });
        /**
         * 공유 중 수정 경고를 모은다.
         *
         * **막는 것은 회차 하한 하나뿐이고 나머지는 전부 경고다**(2026-09-01 방침).
         * 경고는 필드마다 영향이 달라서 한 문장으로 뭉치지 않는다 — 금액은 지난 달
         * 정산까지 소급하고, 날짜는 참여자의 예약 창을 움직이고, 회차 증감은 결제와
         * 어긋난다. 뭉뚱그리면 관리자가 무엇을 확인해야 하는지 알 수 없다.
         *
         * 정산 경고는 여기서 만들지 않는다 — 서버 `settlementImpactFields` 가 정본이고
         * `getSettlementImpactChanges` 가 담당한다.
         */
        const sharedSessionDelta = isSharedSessionEditable
            ? totalSession - membership.totalSession
            : 0;
        // 회차는 증감 방향에 따라 할 말이 달라서 일반 정산 문구에 섞지 않는다.
        // 그 문구("이미 집계된 매출·정산 금액도 함께 바뀝니다")는 강사 지급액이 함께
        // 움직인다는 뜻으로 읽히는데, 회차 변경에는 해당하지 않는다(회당 가격 불변).
        const otherSettlementImpactChanges =
            sharedSessionDelta !== 0
                ? settlementImpactChanges.filter((change) => change.field !== 'totalSession')
                : settlementImpactChanges;

        // 알럿은 하나로 합친다. 확인창을 두 번 띄우면 두 번째는 반사적으로 눌린다.
        const confirmParagraphs: string[] = [];
        if (sharedSessionDelta > 0) {
            confirmParagraphs.push(
                buildSharedActiveSessionIncreaseWarning({
                    fromTotalSession: membership.totalSession,
                    toTotalSession: totalSession,
                }),
            );
        }
        if (sharedSessionDelta < 0) {
            confirmParagraphs.push(
                buildSharedActiveSessionDecreaseWarning({
                    fromTotalSession: membership.totalSession,
                    toTotalSession: totalSession,
                    totalPriceText: convertToKRW(totalPrice).toString(),
                    isPriceEditable:
                        !isLockedByShare('totalPrice') || !isLockedByShare('pricePerSchedule'),
                }),
            );
        }
        /**
         * 참여자 영향 경고.
         *
         * 어느 필드가 대상인지는 **서버 `participantImpactFields` 가 정한다**(계약).
         * 목록이 없으면 경고만 생략하고 **저장은 그대로 진행한다** — 경고는 판정이 아니다.
         *
         * 공유 중일 때만 해당하므로 부분 편집 모드에서만 본다. 공유가 아닌 수강권의
         * 기존 저장 흐름을 건드리지 않는다.
         */
        const changedParticipantFields = isSharedLimitedEdit
            ? ([
                  !dayjs(startDate).isSame(dayjs(membership.activationDate), 'day')
                      ? 'activationDate'
                      : '',
                  !dayjs(expirationDate).isSame(dayjs(membership.expirationDate), 'day')
                      ? 'expirationDate'
                      : '',
                  expiryPolicy !== initialExpiryPolicyRef.current ? 'expiryPolicy' : '',
                  classTime !== membership.period ? 'period' : '',
              ].filter((field) => !!field && !isLockedByShare(field)) as string[])
            : [];
        const participantImpactChanges = getParticipantImpactChanges({
            participantImpactFields,
            changedFields: changedParticipantFields,
        });
        if (participantImpactChanges.length > 0) {
            confirmParagraphs.push(
                buildSharedActiveParticipantImpactWarning(
                    participantImpactChanges.map((change) => change.label),
                ),
            );
        }
        if (otherSettlementImpactChanges.length > 0) {
            confirmParagraphs.push(
                buildSettlementImpactConfirmMessage(otherSettlementImpactChanges),
            );
        }

        if (confirmParagraphs.length > 0 && createTwoButtonAlert) {
            createTwoButtonAlert(
                isSharedLimitedEdit ? '바꾸기 전에 확인해주세요' : '정산 금액도 함께 바뀝니다',
                confirmParagraphs.join('\n\n'),
                true,
                '취소',
                '변경하기',
                undefined,
                submit
            );
            return;
        }

        submit();
    };
    // // 미수금액 = 판매금액 - 결제금액
    // useEffect(() => {
    //     if (isSplitPayment) {
    //         setUnpaidAmount(totalPrice - paymentAmount);
    //     }
    // }, [paymentAmount, totalPrice, isSplitPayment]);

    return (
        <Modal
            whiteViewHeight={800}
            whiteViewWidth={1080}
            setIsVisible={setIsVisible}
            isCloseOutside={false}
            isCloseButton
            onClose={() => setIsVisible(false)}
        >
            <Col className="flex py-[36px] pl-[66px] pr-[98px]">
                <Row className="mb-[24px]">
                    <Subtitle1Bold>수강권 수정하기</Subtitle1Bold>
                </Row>
                {updateRestriction && (
                    <div className="mb-[24px] rounded-[8px] bg-[#FFF5F5] px-[20px] py-[16px] text-[#C73535]">
                        <Body3Regular>{updateRestriction.message}</Body3Regular>
                    </div>
                )}
                {/*
                  공유 중 부분 편집 안내. 실패가 아니라 상태 설명이라 빨강을 쓰지 않는다.
                  무엇이 열려 있고 무엇이 잠겼는지를 한 줄로 말한다 — 잠긴 입력마다
                  같은 문장을 반복하면 화면이 경고로 뒤덮여 아무도 읽지 않는다.
                */}
                {!updateRestriction && sharedActiveEdit && (
                    <div className="mb-[24px] rounded-[8px] bg-[#FFF8E8] px-[20px] py-[16px] text-[#8A6100]">
                        <Body3Regular className="mb-[4px] block">
                            {sharedActiveEdit.notice.title}
                        </Body3Regular>
                        <CaptionLight>{sharedActiveEdit.notice.message}</CaptionLight>
                    </div>
                )}
                <Row className="mb-[32px]">
                    <Col className="mr-[40px]">
                        <Col
                            style={{
                                width: 264,
                                height: 120,
                                background:
                                    BGColor && BGColor?.length > 0
                                        ? `linear-gradient(135deg, ${BGColor[0]} 0%, ${BGColor[1]} 100%)`
                                        : colors.GREY_200,
                                borderRadius: 8,
                                justifyContent: 'center',
                                paddingRight: 24,
                                paddingLeft: 24,
                                marginBottom: 24,
                            }}
                        >
                            <Body2Bold className="text-white">{`${membershipName}`}</Body2Bold>
                        </Col>
                        {/* <NewWhiteRoundButton
                            text="수강권 이름 수정하기"
                            onClick={() => setIsMembershipNameModalVisible(true)}
                            fontType="CaptionRegular"
                            width={136}
                            height={24}
                            mb={52}
                        /> */}
                        {coachName && (
                            <ColTitleAndTextInput
                                title="담당 강사"
                                value={coachName}
                                onChange={() => {}}
                                width={240}
                                height={36}
                                textInputRightIcon={false}
                                textInputLeftIcon={false}
                                disabled
                                mb={40}
                            />
                        )}
                        {memberName && (
                            <ColTitleAndTextInput
                                title="회원"
                                value={memberName}
                                onChange={() => {}}
                                width={240}
                                height={36}
                                textInputRightIcon={false}
                                textInputLeftIcon={false}
                                disabled
                            />
                        )}
                    </Col>
                    <Col className="w-[650px]  rounded-[8px] p-[32px]">
                        <Row className="mb-[48px]">
                            <ShareLockedField locked={isLockedByShare('pricePerSchedule')}>
                            <ColTitleAndTextInput
                                title="회당 가격"
                                numeric
                                isMoney
                                disabled={isLockedByShare('pricePerSchedule')}
                                value={pricePerClass.toString()}
                                onChange={(value) => {
                                    if (value === '') {
                                        value = '0';
                                    }
                                    setPricePerClass(parseInt(value));
                                }}
                                width={240}
                                height={36}
                                onClear={() => {
                                    setPricePerClass(0);
                                }}
                                mr={60}
                                textInputRightIcon={false}
                                isClearIcon={false}
                                // onFocus={() => {
                                //     if (!isCheckUpdate) {
                                //         setIsAlertModalVisible(true);
                                //         setIsCheckUpdate(true);
                                //     }
                                // }}
                            />
                            </ShareLockedField>
                            <ShareLockedField locked={isLockedByShare('totalPrice')}>
                            <ColTitleAndTextInput
                                title="판매 금액"
                                numeric
                                isMoney
                                disabled={isLockedByShare('totalPrice')}
                                value={totalPrice.toString()}
                                onChange={(value) => {
                                    if (value === '') {
                                        value = '0';
                                    }
                                    setTotalPrice(parseInt(value));
                                }}
                                width={240}
                                height={36}
                                onClear={() => {
                                    setTotalPrice(0);
                                }}
                                textInputRightIcon={false}
                                isClearIcon={false}
                                onFocus={() => {
                                    if (!isCheckUpdate) {
                                        setIsAlertModalVisible(true);
                                        setIsCheckUpdate(true);
                                    }
                                }}
                                // onBlur={() => {
                                //     setUnpaidAmount(totalPrice - paymentAmount);
                                // }}
                            />
                            </ShareLockedField>
                        </Row>
                        <Row className="mb-[48px]">
                            <ShareLockedField locked={isLockedByShare('activationDate')}>
                            <ColTitleAndTextInput
                                title="시작 날짜"
                                value={startDate}
                                isCalendar
                                onClickDate={(date) => {
                                    setStartDate(dayjs(date).toISOString());
                                    setMembershipPeriod(dayjs(expirationDate).diff(dayjs(date), 'day'));
                                }}
                                width={240}
                                height={36}
                                mr={60}
                            />
                            </ShareLockedField>
                            <ShareLockedField locked={isLockedByShare('expirationDate')}>
                            <ColTitleAndTextInput
                                title="만료 날짜"
                                value={expirationDate}
                                isCalendar
                                onClickDate={(date) => {
                                    setExpirationDate(dayjs(date).toISOString());
                                    setMembershipPeriod(dayjs(date).diff(dayjs(startDate), 'day'));
                                }}
                                width={240}
                                height={36}
                            />
                            </ShareLockedField>
                        </Row>
                        <Row className=" items-end mb-[48px]">
                            {/* 수강권 기간(일)은 만료 날짜를 만드는 입력이라 만료 날짜와 같이 잠긴다. */}
                            <ShareLockedField locked={isLockedByShare('expirationDate')}>
                            <ColTitleAndTextInput
                                title="수강권 기간"
                                disabled={isLockedByShare('expirationDate')}
                                value={membershipPeriod.toString()}
                                onChange={(value) => {
                                    if (value === '') {
                                        value = '0';
                                    }
                                    setMembershipPeriod(parseInt(value));
                                    setExpirationDate(dayjs(startDate).add(parseInt(value), 'day').toISOString());
                                }}
                                width={140}
                                height={36}
                                mr={32}
                                isClearIcon={false}
                            />
                            </ShareLockedField>
                            <Col>
                                <CaptionLight className="text-WHITE_900">
                                    시작, 만료날짜를 입력하시면 유효기간이 자동으로 계산 되어요!
                                </CaptionLight>
                                <CaptionLight className="text-WHITE_900">
                                    만료 날짜가 지나도 수업 회차가 남으면 만료되지 않아요!
                                </CaptionLight>
                            </Col>
                        </Row>
                        <Col className="mb-[48px]">
                            <Row>
                                <ShareLockedField locked={isLockedByShare('startSessionValue')}>
                                <ColTitleAndTextInput
                                    title="시작 회차"
                                    disabled={isLockedByShare('startSessionValue')}
                                    value={startSession.toString()}
                                    onChange={(value) => {
                                        if (value === '') {
                                            value = '0';
                                        }
                                        setStartSession(parseInt(value));
                                    }}
                                    width={140}
                                    height={36}
                                    mr={60}
                                    textInputRightIcon={false}
                                    isClearIcon={false}
                                />
                                </ShareLockedField>
                                <ShareLockedField locked={isLockedByShare('totalSession')}>
                                <ColTitleAndTextInput
                                    title="총 회차"
                                    numeric
                                    disabled={isLockedByShare('totalSession')}
                                    value={totalSession.toString()}
                                    onChange={(value) => {
                                        if (value === '') {
                                            value = '0';
                                        }
                                        setTotalSession(parseInt(value));
                                    }}
                                    width={140}
                                    height={36}
                                    textInputRightIcon={false}
                                    isClearIcon={false}
                                    mr={60}
                                    onFocus={() => {
                                        if (!isCheckUpdate) {
                                            setIsAlertModalVisible(true);
                                            setIsCheckUpdate(true);
                                        }
                                    }}
                                />
                                </ShareLockedField>
                                <ShareLockedField locked={isLockedByShare('period')}>
                                <ColTitleAndTextInput
                                    title="수업 시간"
                                    disabled={isLockedByShare('period')}
                                    value={classTime}
                                    onChange={(value) => {
                                        if (value === '') {
                                            value = '0';
                                        }
                                        setClassTime(parseInt(value));
                                    }}
                                    width={140}
                                    height={36}
                                    textInputRightIcon={false}
                                    isClearIcon={false}
                                />
                                </ShareLockedField>
                            </Row>
                            {/*
                              총 회차 하한은 서버가 준다(sharedUsage.minimumTotalSession).
                              저장 버튼을 눌러야만 알게 되면 늦다 — 입력 옆에서 미리 알린다.
                              숫자를 넣어야 관리자가 무엇을 정리해야 하는지 안다.
                            */}
                            {typeof minimumTotalSession === 'number' && minimumTotalSession > 0 && (
                                <CaptionLight className="mt-[8px] text-WHITE_900">
                                    이미 사용·예약된 회차가 있어 총 회차는 {minimumTotalSession}회
                                    이상이어야 해요.
                                </CaptionLight>
                            )}
                            {/*
                              공유 중 회차 수정의 두 가지 사실을 입력 옆에서 미리 말한다.
                              저장을 눌러야만 알게 되면 늦고, 특히 "금액은 안 줄어든다"는
                              나중에 장부를 보고 알게 되면 아무도 설명하지 못한다.
                            */}
                            {isSharedSessionEditable && (
                                <>
                                    <CaptionLight className="mt-[8px] text-WHITE_900">
                                        회차를 바꿔도 판매금액({convertToKRW(totalPrice)}원)과 회당
                                        가격은 따라 바뀌지 않아요. 필요하면 위에서 함께 고쳐주세요.
                                    </CaptionLight>
                                    {/*
                                      회차를 늘리는 건 결제 없이 서비스가 나가는 일이다. 막지는
                                      않지만(관리자가 금액도 같이 고칠 수 있다) 결제까지 함께
                                      처리하는 정식 경로가 있다는 건 알려준다. 차단이 아니라 선택지다.
                                    */}
                                    {totalSession > membership.totalSession && (
                                        <Row className="mt-[4px] items-center gap-[6px]">
                                            <CaptionLight className="text-WHITE_900">
                                                결제까지 함께 처리하려면 업그레이드가 정식 경로예요.
                                            </CaptionLight>
                                            {onRequestUpgrade && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setIsVisible(false);
                                                        onRequestUpgrade();
                                                    }}
                                                    style={{
                                                        color: colors.primary,
                                                        fontSize: 12,
                                                        textDecoration: 'underline',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    업그레이드로 처리하기
                                                </button>
                                            )}
                                        </Row>
                                    )}
                                </>
                            )}
                        </Col>
                        {/* 만료 정책 — 그룹/PT 모두 지원 */}
                        <Col className="mb-[48px]">
                            <Row className="items-center mb-[10px] gap-[6px]">
                                <Body3Regular>만료 정책</Body3Regular>
                                <CaptionLight color={colors.LAVEL_1}>
                                    · 수강권별로 변경할 수 있어요
                                </CaptionLight>
                            </Row>
                                <Row className="gap-[8px]" style={{ flexWrap: 'wrap' }}>
                                    {EXPIRY_POLICY_OPTIONS.map((opt) => {
                                        const sel = expiryPolicy === opt.value;
                                        const locked = isLockedByShare('expiryPolicy');
                                        return (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                disabled={locked}
                                                title={locked ? '공유 중에는 바꿀 수 없어요' : undefined}
                                                onClick={() => setExpiryPolicy(opt.value)}
                                                style={{
                                                    opacity: locked ? 0.4 : 1,
                                                    padding: '10px 16px',
                                                    borderRadius: 10,
                                                    border: sel ? '1.5px solid #6366F1' : '1px solid #E1E4E6',
                                                    backgroundColor: sel ? '#EEF2FF' : 'white',
                                                    color: sel ? '#4338CA' : '#6B7280',
                                                    fontSize: 13,
                                                    fontWeight: sel ? 600 : 400,
                                                    cursor: locked ? 'not-allowed' : 'pointer',
                                                    textAlign: 'left',
                                                    minWidth: 160,
                                                    transition: 'all 0.15s',
                                                }}
                                            >
                                                <div style={{ fontWeight: sel ? 700 : 500 }}>{opt.label}</div>
                                                <div style={{ fontSize: 11, color: sel ? '#6366F1' : '#9CA3AF', marginTop: 2 }}>
                                                    {opt.desc}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </Row>
                            </Col>
                        <Row className="mb-[48px]">
                            <ShareLockedField locked={isLockedByShare('salesManager')}>
                            <Col className="mr-[60px]">
                                <Body3Regular className="mb-[8px]">판매담당자</Body3Regular>
                                {!isLoading && salesManager && (
                                    <Dropdown
                                        options={salesManager}
                                        bgColor="white"
                                        className="w-[140px] h-[36px] border border-solid border-[#D4DBE8] justify-between"
                                        borderRadius={50}
                                        arrowColor={colors.primary}
                                        onSelect={(item) => {
                                            setSelectedSalesManager(item.object ?? undefined);
                                        }}
                                        initialSelected={selectedSalesManager?.userId}
                                        isAllOption={false}
                                    />
                                )}
                            </Col>
                            </ShareLockedField>
                            <ShareLockedField locked={isLockedByShare('isNewSales')}>
                            <Col className="mr-[60px]">
                                <Body3Regular className="mb-[8px]">회원 구분</Body3Regular>
                                <Dropdown
                                    initialSelected={memberType}
                                    options={[
                                        { code: 'none', value: '선택' },
                                        { code: 'new', value: '신규' },
                                        { code: 're', value: '재등록' },
                                    ]}
                                    bgColor="white"
                                    className={`w-[140px] h-[36px]  justify-between 
                                                ${
                                                    isRequiredError
                                                        ? 'border border-solid border-red'
                                                        : 'border border-solid border-[#D4DBE8]'
                                                }`}
                                    borderRadius={50}
                                    arrowColor={colors.primary}
                                    isAllOption={false}
                                    onSelect={(item) => {
                                        setMemberType(item.code);
                                    }}
                                />
                            </Col>
                            </ShareLockedField>
                            <ShareLockedField locked={isLockedByShare('initialPaymentDate')}>
                            <Col onClick={() => setIsCalendarOpen(true)}>
                                <Body3Regular color={colors.LAVEL_4} className="mb-[8px]">
                                    결제일자
                                </Body3Regular>
                                <Col
                                    className={`w-[150px] h-[36px] border border-solid border-[#D4DBE8] pl-[16px] justify-center  rounded-full cursor-pointer`}
                                >
                                    <Body3Regular color={colors.LAVEL_4}>
                                        {dayjs(paymentDate).format('YYYY-MM-DD')}
                                    </Body3Regular>
                                </Col>
                            </Col>
                            </ShareLockedField>
                        </Row>
                        {showIssueKindFallbackNotice && (
                            <div className="mb-[24px] flex max-w-[620px] items-start gap-[8px] rounded-[10px] bg-[#FFF7ED] px-[14px] py-[11px] text-[12px] leading-[19px] text-[#9A3412]">
                                <AlertTriangle className="mt-[1px] shrink-0" size={16} color="#C2410C" />
                                <span>{ISSUE_KIND_FALLBACK_CHANGE_NOTICE}</span>
                            </div>
                        )}
                        {hasChangedTransferredMembershipSalesManager && (
                            <div className="mb-[36px] flex max-w-[620px] items-start gap-[8px] rounded-[10px] bg-[#FFF7ED] px-[14px] py-[11px] text-[12px] leading-[19px] text-[#9A3412]">
                                <AlertTriangle className="mt-[1px] shrink-0" size={16} color="#C2410C" />
                                <span>
                                    인계받은 수강권이라 원래 판매 매출의 판매자는 바뀌지 않아요. 매출 목록에서 해당 결제를 눌러 고쳐 주세요.
                                </span>
                            </div>
                        )}
                        <ShareLockedField locked={isLockedByShare('memo')}>
                        <Col>
                            <Body3Regular className="mb-[6px]">결제 메모</Body3Regular>
                            <CaptionLight className="mb-[8px]" color="#62656B">
                                수강권의 메모는 관리자님과 강사님만 확인할 수 있어요!
                            </CaptionLight>
                            <MemoTextInput
                                value={memo}
                                onChange={(e) => setMemo(e.target.value)}
                                height={136}
                                placeholder="기억해야 할 회원님의 상태를 메모해보세요"
                            />
                        </Col>
                        </ShareLockedField>
                    </Col>
                </Row>
                <Row className="w-full justify-end">
                    <Col
                        className="w-[214px] h-[71px] justify-center items-center rounded-[9px] bg-[#F4F5F7] mr-[16px] cursor-pointer"
                        onClick={() => setIsVisible(false)}
                    >
                        <RegularText
                            fontSize={20}
                            lineHeight={24}
                            color={'#3D4244'}
                            onClick={() => setIsVisible(false)}
                        >
                            닫기
                        </RegularText>
                    </Col>

                    {/*
                      저장 중 재클릭을 막는다. 없으면 빠른 중복 클릭이 PATCH 와 변경
                      이력을 두 번 만든다.
                    */}
                    <Col
                        className={`w-[214px] h-[71px] justify-center items-center rounded-[9px] bg-primary ${
                            updateMemberShipMutation.isLoading
                                ? 'opacity-50 cursor-not-allowed'
                                : 'cursor-pointer'
                        }`}
                        onClick={
                            updateMemberShipMutation.isLoading ? undefined : onClickUpdateMembership
                        }
                    >
                        <RegularText fontSize={20} lineHeight={24} color={'white'}>
                            {updateMemberShipMutation.isLoading ? '저장 중...' : '수정 완료'}
                        </RegularText>
                    </Col>
                </Row>
                {isCalendarOpen && (
                    <NewCalendar
                        date={paymentDate || dayjs(paymentDate)}
                        setIsCalendar={setIsCalendarOpen}
                        onClickDate={(day) => {
                            setPaymentDate(dayjs(day).toISOString());
                        }}
                        isAllDate={false}
                        top={200}
                        left={400}
                        selectedDay={dayjs(paymentDate)}
                        isNextMonth
                        isPrevMonth
                        titleFormat="YYYY년 MM월"
                    />
                )}
            </Col>
            {isMembershipNameModalVisible && (
                <ModalUpdateMembershipName
                    onUpdateComplete={(value: string) => {
                        setMembershipName(value);
                        setIsMembershipNameModalVisible(false);
                    }}
                    setIsVisible={setIsMembershipNameModalVisible}
                    value={membershipName}
                />
            )}
            {isSalesManagerModalVisible && (
                <ModalSalesManagerName
                    setIsVisible={setIsSalesManagerModalVisible}
                    onClickConfirm={(name: string) => {
                        postSalesManagerMutation.mutate({
                            membershipId: authObject.centerId,
                            managerName: name,
                        });
                        setSalesManagerName(name);
                    }}
                />
            )}

            {isAlertModalVisible && <ModalUpdateAlert setIsVisible={setIsAlertModalVisible} />}
            {isMemberTypeModalVisible && <ModalAlertMemberType setIsVisible={setIsMemberTypeModalVisible} />}
        </Modal>
    );
};

export default ModalMembershipUpdate;
